import React, { useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { Boxes, Check, X } from "lucide-react";
import {
  deployComposeServices,
  deployProbePorts,
  nginxAddSubdomain,
  nginxListCerts,
} from "../../redux/actionCreator";

/**
 * Point nginx at the services a compose project already runs.
 *
 * Compose publishes each service on a host port; without this you'd read
 * those ports out of the YAML and retype them into vhosts one at a time.
 * Two shapes, because both are legitimate:
 *
 *   Subdomains — one vhost per service (api.example.com → :9102).
 *   Locations  — one vhost, each service under a path (/api → :9102),
 *                with the prefix trimmed so the service sees "/users".
 *
 * Only services with a published TCP port can be proxied; workers and
 * queues are listed but not offered.
 */
const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/^-+|-+$/g, "");

export default function ExposeComposeModal({ show, onHide, projectId, serverId, onDone }) {
  const dispatch = useDispatch();
  const [repoDir, setRepoDir] = useState("");
  const [file, setFile] = useState("docker-compose.yml");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [services, setServices] = useState(null);
  const [certs, setCerts] = useState([]);

  const [shape, setShape] = useState("subdomains"); // subdomains | locations
  const [baseDomain, setBaseDomain] = useState("");
  const [rows, setRows] = useState([]); // {name, port, on, host, path, strip}
  const [mainService, setMainService] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  // port -> { verdict, proxyable }. A published TCP port is not proof the
  // service speaks HTTP, so this is the check worth doing before pointing
  // a vhost at it.
  const [probes, setProbes] = useState({});
  const [probing, setProbing] = useState(false);

  const load = async () => {
    setLoading(true);
    setErr(null);
    setResults(null);
    const res = await dispatch(
      deployComposeServices(projectId, { server_id: serverId, repo_dir: repoDir.trim(), file: file.trim() }),
    );
    setLoading(false);
    if (!res?.status) { setErr(res?.message || "Could not read the compose file"); return; }
    const svcs = res.data.services || [];
    setServices(svcs);
    const usable = svcs.filter((s) => s.exposable);
    setRows(usable.map((s) => ({
      name: s.name,
      port: s.ports[0]?.published,
      on: true,
      host: slug(s.name),
      path: `/${slug(s.name)}`,
      strip: true,
    })));
    setMainService(usable[0]?.name || "");
    const cres = await dispatch(nginxListCerts(projectId, serverId));
    if (cres?.status) setCerts(cres.data?.certs || []);
  };

  const setRow = (i, patch) => setRows(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const testPorts = async () => {
    const ports = rows.map((r) => r.port).filter(Boolean);
    if (!ports.length) return;
    setProbing(true);
    const res = await dispatch(deployProbePorts(projectId, { server_id: serverId, ports }));
    setProbing(false);
    if (!res?.status) { setErr(res?.message || "Probe failed"); return; }
    const map = {};
    for (const r of res.data.results || []) map[r.port] = r;
    setProbes(map);
  };

  // Reuse a wildcard when one covers the names we're about to create —
  // otherwise every subdomain would need its own DNS-01 round trip.
  const certFor = (fqdn) => {
    const name = String(fqdn).toLowerCase();
    const hit = certs.find((c) => (c.domains || []).some((d) => {
      const dd = String(d).toLowerCase();
      if (dd === name) return true;
      if (!dd.startsWith("*.")) return false;
      return name.endsWith(dd.slice(1)) && name.split(".").length === dd.split(".").length;
    }));
    return hit?.dir || "";
  };

  const chosen = rows.filter((r) => r.on && r.port);
  const canApply = !!baseDomain.trim() && chosen.length > 0 &&
    (shape === "subdomains" || !!mainService);

  const apply = async () => {
    setBusy(true);
    setResults(null);
    const base = baseDomain.trim().toLowerCase();
    const out = [];

    if (shape === "subdomains") {
      for (const r of chosen) {
        const fqdn = `${r.host}.${base}`;
        const res = await dispatch(nginxAddSubdomain(projectId, serverId, {
          fqdn,
          upstream: `http://127.0.0.1:${r.port}`,
          cert_dir: certFor(fqdn) || undefined,
        }));
        out.push({ label: fqdn, ok: !!res?.status, message: res?.message });
      }
    } else {
      // One vhost: the main service answers "/", the rest sit underneath.
      const main = chosen.find((r) => r.name === mainService) || chosen[0];
      const others = chosen.filter((r) => r.name !== main.name);
      const res = await dispatch(nginxAddSubdomain(projectId, serverId, {
        fqdn: base,
        upstream: `http://127.0.0.1:${main.port}`,
        cert_dir: certFor(base) || undefined,
        locations: others.map((r) => ({
          path: r.path, type: "port", port: r.port, strip: r.strip,
        })),
      }));
      out.push({ label: base, ok: !!res?.status, message: res?.message });
    }

    setBusy(false);
    setResults(out);
    if (out.some((o) => o.ok)) onDone && onDone();
  };

  const close = () => {
    if (busy) return;
    setServices(null); setRows([]); setResults(null); setErr(null);
    onHide && onHide();
  };

  return (
    <Modal show={show} onHide={close} size="lg" centered backdrop="static">
      <Modal.Header closeButton={!busy}>
        <h6 className="m-0 d-inline-flex align-items-center gap-2">
          <Boxes size={16} /> Expose compose services
        </h6>
      </Modal.Header>
      <Modal.Body>
        <div className="row g-2 align-items-end mb-3">
          <div className="col-md-6">
            <Form.Label className="mb-1">Repo directory on the server</Form.Label>
            <Form.Control
              size="sm" value={repoDir} onChange={(e) => setRepoDir(e.target.value)}
              placeholder="/home/drc" style={{ fontFamily: "ui-monospace, monospace" }}
            />
          </div>
          <div className="col-md-4">
            <Form.Label className="mb-1">Compose file</Form.Label>
            <Form.Control
              size="sm" value={file} onChange={(e) => setFile(e.target.value)}
              style={{ fontFamily: "ui-monospace, monospace" }}
            />
          </div>
          <div className="col-md-2">
            <Button size="sm" variant="light" className="w-100"
              onClick={load} disabled={loading || !repoDir.trim()}>
              {loading ? <Spinner size="sm" animation="border" /> : "Read"}
            </Button>
          </div>
        </div>

        {err && <div className="alert alert-danger py-2 small">{err}</div>}

        {services && (
          <>
            {services.some((s) => !s.exposable) && (
              <div className="small text-muted mb-2">
                Not offered (no published port):{" "}
                {services.filter((s) => !s.exposable).map((s) => <code key={s.name} className="me-1">{s.name}</code>)}
              </div>
            )}

            <div className="d-flex gap-3 mb-2">
              <Form.Check type="radio" name="shape" id="shape-sub"
                checked={shape === "subdomains"} onChange={() => setShape("subdomains")}
                label="One subdomain per service" />
              <Form.Check type="radio" name="shape" id="shape-loc"
                checked={shape === "locations"} onChange={() => setShape("locations")}
                label="One domain, a path per service" />
              <Button size="sm" variant="light" className="ms-auto"
                onClick={testPorts} disabled={probing || !rows.length}>
                {probing ? <Spinner size="sm" animation="border" /> : "Test ports"}
              </Button>
            </div>
            {Object.keys(probes).length > 0 && Object.values(probes).some((p) => !p.proxyable) && (
              <div className="alert alert-warning py-2 small">
                A port that listens but doesn't answer HTTP can't sit behind an
                nginx proxy — redis and databases look like this. Don't select those.
              </div>
            )}

            <Form.Label className="mb-1">
              {shape === "subdomains" ? "Base domain" : "Domain"}
            </Form.Label>
            <Form.Control
              size="sm" className="mb-3" value={baseDomain}
              onChange={(e) => setBaseDomain(e.target.value)}
              placeholder={shape === "subdomains" ? "smarthatch.xyz" : "app.smarthatch.xyz"}
              style={{ fontFamily: "ui-monospace, monospace" }}
            />

            {rows.map((r, i) => (
              <div key={r.name} className="d-flex gap-2 align-items-center mb-2">
                <Form.Check
                  type="checkbox" checked={r.on} id={`svc-${r.name}`}
                  onChange={(e) => setRow(i, { on: e.target.checked })}
                />
                <code className="small" style={{ minWidth: 110 }}>{r.name}</code>
                <span className="text-muted small" style={{ minWidth: 60 }}>:{r.port}</span>
                {probes[r.port] && (
                  <span
                    className={`badge ${probes[r.port].proxyable ? "bg-success" : "bg-warning text-dark"}`}
                    style={{ minWidth: 150 }}
                    title="Probed from the server over 127.0.0.1"
                  >
                    {probes[r.port].verdict}
                  </span>
                )}
                {shape === "subdomains" ? (
                  <div className="d-flex align-items-center gap-1 flex-grow-1">
                    <Form.Control size="sm" value={r.host} disabled={!r.on}
                      onChange={(e) => setRow(i, { host: e.target.value })}
                      style={{ fontFamily: "ui-monospace, monospace", maxWidth: 160 }} />
                    <span className="small text-muted">.{baseDomain || "domain"}</span>
                  </div>
                ) : (
                  <div className="d-flex align-items-center gap-2 flex-grow-1">
                    <Form.Control size="sm" value={r.path} disabled={!r.on || r.name === mainService}
                      onChange={(e) => setRow(i, { path: e.target.value })}
                      style={{ fontFamily: "ui-monospace, monospace", maxWidth: 160 }} />
                    {r.name === mainService
                      ? <span className="badge bg-secondary">serves /</span>
                      : (
                        <Form.Check type="switch" id={`strip-${r.name}`} checked={!!r.strip} disabled={!r.on}
                          onChange={(e) => setRow(i, { strip: e.target.checked })}
                          label={<span className="small">trim prefix</span>} />
                      )}
                  </div>
                )}
              </div>
            ))}

            {shape === "locations" && (
              <div className="mt-2">
                <Form.Label className="mb-1 small">Which service answers <code>/</code>?</Form.Label>
                <Form.Select size="sm" value={mainService} onChange={(e) => setMainService(e.target.value)}
                  style={{ maxWidth: 240 }}>
                  {rows.filter((r) => r.on).map((r) => <option key={r.name} value={r.name}>{r.name}</option>)}
                </Form.Select>
              </div>
            )}

            {baseDomain.trim() && chosen.length > 0 && (
              <div className="small text-muted mt-3">
                {certFor(shape === "subdomains" ? `${chosen[0].host}.${baseDomain.trim()}` : baseDomain.trim())
                  ? <>An existing certificate covers these — they'll be created on HTTPS with no certbot run.</>
                  : <>No certificate covers these yet; they'll be created on plain HTTP. Issue a wildcard first to get HTTPS immediately.</>}
              </div>
            )}

            {results && (
              <div className="mt-3">
                {results.map((r) => (
                  <div key={r.label} className={`small ${r.ok ? "text-success" : "text-danger"}`}>
                    {r.ok ? <Check size={13} /> : <X size={13} />} {r.label}
                    {r.message ? ` — ${r.message}` : ""}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={close} disabled={busy}>Close</Button>
        <Button variant="primary" onClick={apply} disabled={busy || !canApply}>
          {busy ? <Spinner size="sm" animation="border" />
                : shape === "subdomains" ? `Create ${chosen.length} subdomain(s)` : "Create vhost"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
